import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Button } from "antd";
import { useSearchParams } from "react-router-dom";
import { svg } from "../../../../Assets/svg";
import { renderFormLabel } from "../../../../Components/Components";
import { useAppDispatch } from "../../../../store";
import {
  createPaymentMilestioneAPI,
  updateClientPaymentMilestoneAPI,
} from "../clientDistributorsApis";
import {
  CLIENT_FILTER_KEY,
  CLIENT_REFETCH_KEY,
  CLIENT_SCROLL_KEY,
} from "../clientDistributorTypes";

interface Props {
  open: boolean;
  onClose: () => void;
  clientId: string; // ✅ Needed to attach milestone to specific client
  refreshData?: () => void; // ✅ To update list after save
  editData?: any;
}

export const AddPaymentMilestoneModal: React.FC<Props> = ({
  open,
  onClose,
  clientId,
  refreshData,
  editData,
}) => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const quoteId = searchParams.get("quote_id");
  const orderId = searchParams.get("order_id");
  const contextualParams = quoteId
    ? { quote_id: quoteId }
    : orderId
    ? { order_id: orderId }
    : undefined;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      if (editData) {
        form.setFieldsValue({
          milestoneNumber: editData.milestone_number?.toString(),
          milestoneName: editData.milestone_name,
          paymentPercent: editData.value,
        });
      } else {
        form.resetFields();
      }
    }
  }, [open, editData, form]);

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const payload: any = {
        customer_id: clientId,
        milestone_number: Number(values.milestoneNumber),
        milestone_name: values.milestoneName,
        value: Number(values.paymentPercent),
      };

      const commonCallbacks = {
        onSuccess: () => {
          sessionStorage.removeItem(CLIENT_FILTER_KEY);
          sessionStorage.removeItem(CLIENT_SCROLL_KEY);
          sessionStorage.setItem(CLIENT_REFETCH_KEY, "1");
          setLoading(false);
          onClose();
          refreshData?.();
        },
        onError: () => {
          setLoading(false);
        },
      };

      if (editData) {
        // --- UPDATE MODE ---
        payload.id = editData._id;
        dispatch(
          updateClientPaymentMilestoneAPI({
            payload: payload,
            params: contextualParams,
            ...commonCallbacks,
          }),
        );
      } else {
        // --- CREATE MODE ---
        dispatch(
          createPaymentMilestioneAPI({
            payload: payload,
            params: contextualParams,
            ...commonCallbacks,
          }),
        );
      }
    } catch (error) {
      console.log("Validation Failed:", error);
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      title={
        <div className="text-center fs-4 fw-bolder lh-m">
          {editData ? "Update Payment Milestone" : "Add Payment Milestone"}
        </div>
      }
      closeIcon={svg?.closeIcon}
      width={"33.75rem"}
      className="commonModalClass"
    >
      <Form layout="vertical" form={form} className="page-form">
        {/* Milestone Number */}
        <Form.Item
          name="milestoneNumber"
          label={renderFormLabel("Milestone Number", false)}
          rules={[
            {
              required: true,
              message: "Enter Milestone Number",
              whitespace: true, // ✅ Prevents only spaces
            },
          ]}
          required={false} // Hides default star
        >
          <Input
            className="form-control form-control-lg"
            placeholder="Enter Milestone Number"
            type="number"
          />
        </Form.Item>

        {/* Milestone Name */}
        <Form.Item
          name="milestoneName"
          label={renderFormLabel("Milestone Name", false)}
          rules={[
            {
              required: true,
              message: "Enter Milestone Name",
              whitespace: true, // ✅ Prevents only spaces
            },
          ]}
          required={false}
        >
          <Input
            className="form-control form-control-lg"
            placeholder="Enter Milestone Name"
          />
        </Form.Item>

        {/* Payment % */}
        <Form.Item
          name="paymentPercent"
          label={renderFormLabel("Payment %", false)}
          rules={[
            { required: true, message: "Enter Percentage" },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                // ✅ Check for whitespace/empty string manually since type="number" doesn't support whitespace:true prop well
                if (value.toString().trim() === "") {
                  return Promise.reject(new Error("Enter Percentage"));
                }
                // ✅ Check if positive
                if (Number(value) <= 0) {
                  return Promise.reject(
                    new Error("Percentage must be greater than 0"),
                  );
                }
                if (value && Number(value) > 100) {
                  return Promise.reject(new Error("Cannot exceed 100%"));
                }
                return Promise.resolve();
              },
            },
          ]}
          required={false}
        >
          <Input
            type="number" // improved UX for mobile
            className="form-control form-control-lg"
            placeholder="Enter Value"
            prefix={<span className="text-white">%</span>}
            min={0} // HTML attribute helper
            onWheel={(e: any) => e.currentTarget.blur()}
          />
        </Form.Item>

        {/* Buttons */}
        <div className="d-flex justify-content-center gap-3 mt-4">
          <Button
            className="btn btn-lg btn-outline-secondary h-auto px-5"
            onClick={onClose}
            disabled={loading}
          >
            CLOSE
          </Button>
          <Button
            className="btn btn-lg btn-primary h-auto px-5"
            type="primary"
            onClick={onSave}
            loading={loading} // Show spinner
          >
            {editData ? "UPDATE" : "SAVE"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};
